import CardCommon from '@op/components/CardCommon';
import { cardIcon, tableIcon } from '@op/constants/chart';
import React, { useEffect, useState } from 'react';
import { useHistory, useIntl } from 'umi';
import FigureCard from '../components/FigureCard/index';
import './index.scss';
import { useTrendingRequest } from '../hooks/useTrendingRequest';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import TableCommonHook from '@op/components/TableCommonHook';
import { useTrendingColumn } from '@op/components/TableCommonHook/hooks/useTrendingColumn';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import {
  useDrilldownParams,
  EDrilldownMode,
} from '@op/hooks/useDrilldownParams';
import { SortBy } from 'tea-component/lib/table/addons';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import {
  getLogValue,
  useRequestLatestEditLog,
} from '@op/pages/feeds/hooks/useFeedsRequest';
import { SentimentType } from '@op/types/common';
import { useUpdateEffect } from 'ahooks';
import { goToFeedsReviewsDetail } from '@op/utils/url';
import SvgIcon from '@home/components/SvgIcon';
import { useTrendingLoading } from '@op/models/loading/useTrendingLoading';

const sortByOptions = [
  { value: 'view', text: 'Views' },
  { value: 'engagement', text: 'Engagement' },
  { value: 'follower_number', text: 'Followers' },
  { value: 'tweets_reply', text: 'Comments' },
  { value: 'comment_time', text: 'Date' },
];
const sortColumns = sortByOptions.map((val) => val.value);
const extraFilter = { review_types: ['posts_text'] };

export default function TrendingPosts({
  filter,
  wholeGameInfo,
  language,
  hasMore: propsHasMore,
  gameInfo,
  titleOnClickPath,
  jumpType,
  initialSortBy,
  initialIsActive = false,
  initialView = 'card',
  postsStateOuterRef,
}: any) {
  const { formatMessage } = useIntl();
  const [isActive, setIsActive] = useState(initialIsActive);
  const [sortBy, setSortBy] = useState<SortBy<any>>(
    initialSortBy || { by: 'engagement', order: 'desc' },
  );
  const [view, setView] = useState(initialView);

  // 同步子组件状态到父级 ref，用于离开页面时保存
  useEffect(() => {
    if (postsStateOuterRef) {
      postsStateOuterRef.current = { sortBy, isActive, view };
    }
  }, [sortBy, isActive, view, postsStateOuterRef]);

  const handleClick = () => {
    setIsActive(!isActive);
  };

  const { data, loading } = useTrendingRequest({
    filter,
    sortBy,
    extraFilter,
    type: 'feeds',
  });

  // 首次加载完成时通知父组件
  useTrendingLoading('trendingPosts', loading ?? false);

  const {
    logCommentMap,
    loading: logLoading,
    requestLogLastEditLog,
  } = useRequestLatestEditLog({
    data: data.list || [],
    type: ['sentiment'],
    needRequest: true,
  });

  useUpdateEffect(() => {
    requestLogLastEditLog();
  }, [view]);

  const { columns } = useTrendingColumn({
    language,
    wholeGameInfo,
    keys: [
      'channelName',
      'post',
      'language',
      'authorCountry',
      'commentTime',
      'view',
      'engagement',
      'subscribe',
      'tweetsReply'
    ],
    statistics: data?.statistics,
    logBtnId: 'TrendingPosts',
    logCommentMap,
    logLoading,
    gameInfo,
  });

  const history = useHistory();
  const { goPage } = useDrilldownParams({ jumpType });
  const navigatePage = (path?: string) => {
    const gameid = gameInfo?.edition_unified_id;
    return goPage({
      path: path
        ? `${path}${gameid ? `?gameid=${gameid}` : ''}`
        : `/opinion/Feeds/Feeds?${gameid ? `gameid=${gameid}&` : ''
        }tab=socialMedia&by=${sortBy.by}&order=${sortBy.order}`,
      filter: {
        ...filter,
        ...extraFilter,
      },
      mode: EDrilldownMode.Local,
    });
  };

  const hasMore = propsHasMore && (data?.total || 0) > 4;

  const { downloadLegacy, downloading } = useDownloadParams();

  return (
    <CardCommon
      title={formatMessage({ id: 'Trending Posts' })}
      operations={[cardIcon, tableIcon]}
      hasDownload={data?.list?.length > 0}
      hasTitleArrow
      className={`card--trend-posts ${propsHasMore ? 'card--trend-video' : ''}`}
      handleOperationsOnClick={(item) => {
        setView(item?.symbol || 'card');
      }}
      handleDownloadOnClick={async () => {
        const downloadLimit = 50;

        // if (!data?.total) return;

        const params = {
          page: 1,
          page_size: downloadLimit,
          sort_item: sortBy.by,
          sort_model: sortBy.order,
          noChangePageSize: true,
        };
        const downLoadParams = {
          ...filter,
          ...params,
          ...extraFilter,
        };

        const gameName = wholeGameInfo?.entity_name || '';
        const fileName = `${gameName} Posts - DataBrain Opinions`;
        await downloadLegacy({
          total: downloadLimit,
          limit: downloadLimit,
          fileName,
          params: downLoadParams,
          downloadFunc: 'getOpinionVideoComments',
          sheetName: 'Video',
        });
      }}
      downloadLoading={downloading}
      loading={loading}
      empty={data?.list?.length === 0}
      loadingHeight={450}
      emptyHeight={450}
      handleTitleOnClick={() =>
        titleOnClickPath ? navigatePage(titleOnClickPath) : navigatePage()
      }
      logBtnId={'TrendingPosts'}
    >
      {/* 点击更多切换 active */}
      <div className={`trend-video-list-wrapper ${isActive || !hasMore ? 'active' : ''}`}
      >
        {view === 'card' ? (
          <>
            <SortBySelect
              defaultParams={{
                options: sortByOptions,
                btnTextPrefix: formatMessage({ id: 'Sort By' }),
              }}
              defaultValue={sortBy.by}
              onChange={(_key: any, val: string) => {
                setSortBy({ by: val, order: 'desc' });
                buriedBtnPoint('TPTP_Sort', { by: val, order: 'desc' });
              }}
            />
            <div className={`trend-posts-list ${propsHasMore ? 'trend-video-list' : ''}`}
            >
              {data?.list?.map((item: any, index: number) => {
                const sentiment = getLogValue(
                  logCommentMap,
                  item,
                  'latest_sentiment',
                ) as SentimentType;

                return (
                  <div className="trend-posts-item" key={index}>
                    <FigureCard
                      size="large"
                      item={item}
                      loading={logLoading}
                      updateSentiment={sentiment}
                      wholeGameInfo={wholeGameInfo}
                      language={language}
                      navigatePage={(e: any) => {
                        buriedBtnPoint('TPTP_ClickCard');
                        goToFeedsReviewsDetail(
                          history,
                          item,
                          gameInfo?.edition_unified_id
                            ? { language, gameid: gameInfo?.edition_unified_id }
                            : { language },
                          jumpType,
                        );
                      }}
                      buriedBtnPointId={'TPTP_GoToURL'}
                    ></FigureCard>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="trend-video-table">
            <TableCommonHook
              recordKey={'comment_uin'}
              records={data?.list || []}
              total={10}
              columns={columns}
              sortColumns={sortColumns}
              hasPageable={false}
              loading={loading}
              sortInfo={[sortBy, setSortBy]}
              logBtnId={'TrendingPosts'}
            />
          </div>
        )}
        {/* 点击切换展示更多 */}
        {hasMore && (
          <div className="dbo-switch-show" onClick={handleClick}>
            {isActive ? (
              <>
                <span>{formatMessage({ id: 'Close-1' })}</span>
                <SvgIcon type="arrow-up" />
              </>
            ) : (
              <>
                <span>{formatMessage({ id: 'More' })}</span>
                <SvgIcon type="arrow-down" />
              </>
            )}
          </div>
        )}
      </div>
    </CardCommon>
  );
}
